import React, { useState, useEffect } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getFirestore, collection, addDoc, serverTimestamp, 
  doc, onSnapshot, updateDoc, deleteDoc
} from 'firebase/firestore';
import { 
  getAuth, onAuthStateChanged, signInAnonymously, signInWithCustomToken 
} from 'firebase/auth';
import { 
  Package, Search, Plus, Edit, Trash2, Save, X, 
  AlertCircle, CheckCircle2, Upload, Download, 
  Layers, Factory, Truck, Tag, Euro, Database,
  List, Link as LinkIcon, Star, ChevronRight, 
  ArrowRightLeft, FileText, Wrench, ShoppingCart, 
  Activity, Calendar, MapPin, Filter, FileCheck,
  Users, Calculator, Globe, ArrowRight
} from 'lucide-react';

// --- Firebase Initialisierung ---
const getGlobal = (key) => {
  if (typeof window !== 'undefined' && window[key] !== undefined) {
    return window[key];
  }
  return null;
};

const firebaseConfigRaw = getGlobal('__firebase_config');
const firebaseConfig = firebaseConfigRaw ? JSON.parse(firebaseConfigRaw) : {};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);
const db = getFirestore(app);

// --- Hilfsfunktion: Nummern sicher aus Strings mit Komma konvertieren ---
const parseNum = (val) => {
  if (val === null || val === undefined || val === '') return 0;
  const parsed = parseFloat(String(val).replace(',', '.'));
  return isNaN(parsed) ? 0 : parsed;
};

// ============================================================================
// INITIALER STATUS FÜR EINEN ARTIKEL
// ============================================================================
const initialArticleState = {
  articleId: '',           
  name: '',                
  description: '',         
  materialType: 'Handelsware', 
  category: '',            
  partType: 'Einkaufsteil', 
  usageCategory: 'Standard',
  status: 'aktiv',          
  replacedBy: '',           
  replaces: '',             
  
  manufacturer: '',
  manufacturerPartId: '',
  dinStandard: '',

  // Disposition
  dispoType: 'Bedarfsgesteuert',
  dispoTrigger: 'Meldebestand',
  leadTime: '',

  // Einheiten & Lager
  purchasingUnit: 'Stk',
  storageUnit: 'Stk',
  salesUnit: 'Stk',
  stock: 0,                
  minStock: 0,
  storageLocations: [],     

  // Einkauf & Verkauf
  customsTariffNumber: '',
  salesPrice: '',
  taxRate: '19',
  customFields: [],         
  suppliers: [],            
  customers: [],             
  tieredPrices: [],          
  regionalPrices: [],        
  
  // Kalkulation (Neu: Unterteilt in Make und Buy)
  calculation: {
    make: { productionCosts: '', overhead: '', markup: '20' },
    buy: { basePrice: '', overhead: '', markup: '20' }
  },

  // Konstruktion
  construction: {
    drawingNumber: '',
    material: '',
    color: '',
    weight: '',
    length: '',              
    width: '',               
    height: ''               
  },

  bom: [],                   
  documents: []              
};

// ============================================================================
// ARTIKELSTAMM COMPONENT
// ============================================================================
export const Articles = ({ userId, companyId }) => {
  const [articles, setArticles] = useState([]);
  const [dbSuppliers, setDbSuppliers] = useState([]); 
  const [activeTab, setActiveTab] = useState('overview'); 
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState(initialArticleState);
  
  // --- Bearbeitungsmodus und separate Artikelnummer für die Suche ---
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [readOnlySearchId, setReadOnlySearchId] = useState('');
  const [sidebarSearchId, setSidebarSearchId] = useState(''); 
  
  // Suchfelder (Listenansicht)
  const [searchGeneral, setSearchGeneral] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState({
    materialType: '', category: '', manufacturer: '', manufacturerPartId: '', supplier: '', supplierPartId: '', dinStandard: ''
  });
  
  // Globale Plankarte
  const [globalPlanningSearch, setGlobalPlanningSearch] = useState('');
  const [globalPlanningArticle, setGlobalPlanningArticle] = useState(null);

  // Temporäre States für Listen-Einträge in Tabs
  const [newSupplier, setNewSupplier] = useState({ supplierId: '', name: '', partId: '', price: '', currency: 'EUR', leadTime: '' });
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  
  // Temporäre States für Verkauf
  const [newCustomer, setNewCustomer] = useState({ name: '', partId: '', price: '', currency: 'EUR' });
  const [newTierPrice, setNewTierPrice] = useState({ quantity: '', price: '' });
  const [newRegionalPrice, setNewRegionalPrice] = useState({ region: 'Europa (EU)', customRegion: '', price: '', currency: 'EUR' });

  // Suche für Nachfolger
  const [replacedBySearchTerm, setReplacedBySearchTerm] = useState('');
  const [showReplacedByDropdown, setShowReplacedByDropdown] = useState(false);

  // States für Modal
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierModalSearch, setSupplierModalSearch] = useState('');
  const [supplierModalCategory, setSupplierModalCategory] = useState('');
  
  const [newBomItem, setNewBomItem] = useState({ articleId: '', quantity: 1 });
  const [newCustomField, setNewCustomField] = useState({ name: '', value: '' });
  const [newLocation, setNewLocation] = useState({ location: '', bin: '', quantity: 0 });
  const [newDocument, setNewDocument] = useState({ title: '', category: 'Einkauf', type: 'Zertifikat (z.B. FDA, 3.1)', note: '' });

  const [feedback, setFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [activeUser, setActiveUser] = useState(null);

  // Authentifizierung
  useEffect(() => {
    if (userId) { setActiveUser({ uid: userId }); return; }
    const initAuth = async () => {
      if (auth.currentUser) { setActiveUser(auth.currentUser); return; }
      try {
        const token = getGlobal('__initial_auth_token');
        if (token) await signInWithCustomToken(auth, token);
        else await signInAnonymously(auth);
      } catch (err) { console.error("Auth Error:", err); }
    };
    initAuth();
    const unsubscribe = onAuthStateChanged(auth, setActiveUser);
    return () => unsubscribe();
  }, [userId]);

  // Daten laden
  useEffect(() => {
    if (!activeUser || !companyId) return;
    
    const unsubscribeArticles = onSnapshot(collection(db, 'companies', companyId, 'articles'), 
      (snapshot) => {
        const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        loaded.sort((a, b) => (b.articleId || '').localeCompare(a.articleId || ''));
        setArticles(loaded);
      }, 
      (error) => console.error("Firestore Error (Articles):", error)
    );

    const unsubscribeSuppliers = onSnapshot(collection(db, 'companies', companyId, 'suppliers'), 
      (snapshot) => {
        const loaded = snapshot.docs.map(doc => {
          const data = doc.data();
          const supplierName = data.name || data.companyName || data.company || data.firma || data.supplierName || 'Unbekannt';
          return { id: doc.id, ...data, name: supplierName };
        });
        loaded.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDbSuppliers(loaded);
      }, 
      (error) => console.error("Firestore Error (Suppliers):", error)
    );

    return () => {
      unsubscribeArticles();
      unsubscribeSuppliers();
    };
  }, [activeUser, companyId]);

  // Sidebar Suche synchronisieren
  useEffect(() => {
    if (currentId) setSidebarSearchId(formData.articleId);
    else setSidebarSearchId('');
  }, [currentId, formData.articleId]);

  const generateNextId = () => {
    if (articles.length === 0) return "100000";
    const ids = articles.map(a => parseInt(String(a.articleId || '').replace(/\D/g, ''))).filter(id => !isNaN(id) && id >= 100000);
    return `${(ids.length > 0 ? Math.max(...ids) : 99999) + 1}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCalculationMakeChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev, calculation: { ...prev.calculation, make: { ...(prev.calculation?.make || {}), [name]: value } }
    }));
  };

  const handleCalculationBuyChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev, calculation: { ...prev.calculation, buy: { ...(prev.calculation?.buy || {}), [name]: value } }
    }));
  };

  const handleConstructionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev, construction: { ...(prev.construction || initialArticleState.construction), [name]: value }
    }));
  };

  const handleAddNew = () => {
    setFormData({ ...initialArticleState, articleId: generateNextId() });
    setCurrentId(null);
    setIsEditingMode(true); 
    setActiveTab('general');
    setFeedback(null);
  };

  const openArticle = (article) => {
    setFormData({ ...initialArticleState, ...article });
    setCurrentId(article.id);
    setReadOnlySearchId(article.articleId); 
    setIsEditingMode(false); 
    
    // Tab-Retention Logik
    let nextTab = activeTab;
    if (nextTab === 'bom' && article.partType !== 'Eigenfertigungsteil') {
      nextTab = 'general';
    } else if (nextTab === 'overview' || nextTab === 'global_planning') {
      nextTab = 'general';
    }
    setActiveTab(nextTab);
    
    setFeedback(null);
    setReplacedBySearchTerm('');
  };

  const handleSave = async () => {
    if (!activeUser || !companyId) return;
    if (!formData.name || !formData.articleId) {
      setFeedback({ type: 'error', text: 'Bitte Artikelnummer und Bezeichnung angeben.' });
      return;
    }
    setIsSaving(true); setFeedback(null);
    try {
      const articleData = { ...formData, updatedAt: serverTimestamp(), createdBy: activeUser.uid };
      const colRef = collection(db, 'companies', companyId, 'articles');

      if (currentId) {
        await updateDoc(doc(colRef, currentId), articleData);
        setFeedback({ type: 'success', text: 'Artikel erfolgreich aktualisiert!' });
      } else {
        const newDoc = await addDoc(colRef, { ...articleData, createdAt: serverTimestamp() });
        setCurrentId(newDoc.id);
        setFeedback({ type: 'success', text: 'Neuer Artikel angelegt!' });
      }
      
      if (formData.status === 'ersetzt' && formData.replacedBy) {
         const succ = articles.find(a => a.articleId === formData.replacedBy);
         if (succ && succ.id) await updateDoc(doc(colRef, succ.id), { replaces: formData.articleId });
      }
      
      setIsEditingMode(false); 
      setReadOnlySearchId(formData.articleId);
      setTimeout(() => setFeedback(null), 3000);
    } catch (error) {
      setFeedback({ type: 'error', text: `Fehler: ${error.message}` });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!companyId) return;
    if (window.confirm(`Möchten Sie den Artikel "${name}" wirklich löschen?`)) {
      await deleteDoc(doc(db, 'companies', companyId, 'articles', id));
      if (currentId === id) setActiveTab('overview');
    }
  };

  // --- Sub-Listen Funktionen ---
  const addItemToList = (listName, item, resetStateFn, emptyState) => {
    setFormData(prev => ({ ...prev, [listName]: [...(prev[listName] || []), { ...item, id: Date.now().toString() }] }));
    resetStateFn(emptyState);
  };

  const removeItemFromList = (listName, id) => {
    setFormData(prev => ({ ...prev, [listName]: prev[listName].filter(item => item.id !== id) }));
  };

  // --- Kalkulations-Hilfen ---
  const getBomCost = () => {
    return (formData.bom || []).reduce((sum, item) => {
      const refArt = articles.find(a => a.articleId === item.articleId);
      let cost = 0;
      if (refArt && refArt.suppliers && refArt.suppliers.length > 0) {
        const mainSup = refArt.suppliers.find(s => s.isMain) || refArt.suppliers[0];
        cost = parseNum(mainSup.price);
      }
      return sum + (cost * parseNum(item.quantity));
    }, 0);
  };

  const getSupplierCost = () => {
    if (formData.suppliers && formData.suppliers.length > 0) {
      const mainSup = formData.suppliers.find(s => s.isMain) || formData.suppliers[0];
      return parseNum(mainSup.price);
    }
    return 0;
  };

  const handleApplySalesPrice = (newSalesPrice) => {
    const oldSalesPrice = parseNum(formData.salesPrice);
    let updatedTieredPrices = [...(formData.tieredPrices || [])];
    
    // Prüfen ob Staffelpreise angepasst werden sollen
    if (updatedTieredPrices.length > 0) {
      if (oldSalesPrice > 0) {
        const shouldScale = window.confirm(`Es wurden ${updatedTieredPrices.length} Staffelpreise gefunden.\n\nMöchten Sie diese proportional zum neuen Standard-VK-Preis (${newSalesPrice.toFixed(2)} EUR) hochrechnen lassen?`);
        
        if (shouldScale) {
          const ratio = newSalesPrice / oldSalesPrice;
          updatedTieredPrices = updatedTieredPrices.map(tier => ({
            ...tier,
            price: (parseNum(tier.price) * ratio).toFixed(2)
          }));
        }
      } else {
        alert("Hinweis: Da bisher kein alter Standard-Verkaufspreis hinterlegt war, können die bestehenden Staffelpreise nicht automatisch proportional hochgerechnet werden. Bitte passen Sie diese manuell an.");
      }
    }
    
    setFormData(prev => ({ 
      ...prev, 
      salesPrice: newSalesPrice.toFixed(2),
      tieredPrices: updatedTieredPrices
    }));
    
    setFeedback({ type: 'success', text: 'Kalkulierter Preis wurde als Standard-VK übernommen!' });
    setTimeout(() => setFeedback(null), 3000);
  };

  // --- Globale Plankarte Suche ---
  const handleGlobalPlanningExecute = () => {
    const art = articles.find(a => a.articleId === globalPlanningSearch.trim());
    if (art) {
      setGlobalPlanningArticle(art);
      setFeedback(null);
    } else {
      setGlobalPlanningArticle(null);
      setFeedback({ type: 'error', text: 'Artikelnummer nicht gefunden.' });
    }
  };

  // --- Lieferanten Hilfsfunktionen ---
  const supplierCategories = [...new Set(dbSuppliers.map(s => s.category || s.rubrik).filter(Boolean))];

  const handleSelectSupplier = (sup) => {
    const safeName = String(sup.name || '');
    setNewSupplier({...newSupplier, supplierId: sup.id, name: safeName});
    setSupplierSearchTerm(safeName);
    setShowSupplierDropdown(false);
    setShowSupplierModal(false);
  };

  // --- Filtern ---
  const filtered = articles.filter(a => {
    const termGen = String(searchGeneral || '').toLowerCase();
    let match = termGen === '' || 
      String(a.name || '').toLowerCase().includes(termGen) ||
      String(a.articleId || '').toLowerCase().includes(termGen) ||
      String(a.category || '').toLowerCase().includes(termGen);
    
    if (match && showAdvancedSearch) {
      if (advancedSearch.materialType && a.materialType !== advancedSearch.materialType) match = false;
      if (advancedSearch.category && !String(a.category || '').toLowerCase().includes(String(advancedSearch.category || '').toLowerCase())) match = false;
      if (advancedSearch.manufacturer && !String(a.manufacturer || '').toLowerCase().includes(String(advancedSearch.manufacturer || '').toLowerCase())) match = false;
      if (advancedSearch.manufacturerPartId && !String(a.manufacturerPartId || '').toLowerCase().includes(String(advancedSearch.manufacturerPartId || '').toLowerCase())) match = false;
      if (advancedSearch.dinStandard && !String(a.dinStandard || '').toLowerCase().includes(String(advancedSearch.dinStandard || '').toLowerCase())) match = false;
      
      if (advancedSearch.supplier || advancedSearch.supplierPartId) {
        const hasSup = a.suppliers?.some(sup => {
          const ms = advancedSearch.supplier === '' || String(sup.name || '').toLowerCase().includes(String(advancedSearch.supplier || '').toLowerCase());
          const mp = advancedSearch.supplierPartId === '' || String(sup.partId || '').toLowerCase().includes(String(advancedSearch.supplierPartId || '').toLowerCase());
          return ms && mp;
        });
        if (!hasSup) match = false;
      }
    }
    return match;
  });

  const resetAdvancedSearch = () => {
    setAdvancedSearch({ materialType: '', category: '', manufacturer: '', manufacturerPartId: '', supplier: '', supplierPartId: '', dinStandard: '' });
  };

  // --- Layout Definitionen ---
  const isEditingView = !['overview', 'global_planning'].includes(activeTab);
  
  const editingTabs = [
    { id: 'general', label: 'Allgemein', icon: <Database className="w-4 h-4" /> },
    { id: 'disposition', label: 'Disposition', icon: <Activity className="w-4 h-4" /> },
    { id: 'purchasing', label: 'Einkauf', icon: <Truck className="w-4 h-4" /> },
    { id: 'sales', label: 'Verkauf', icon: <Euro className="w-4 h-4" /> },
    { id: 'calculation', label: 'Kalkulation', icon: <Calculator className="w-4 h-4" /> },
    { id: 'inventory', label: 'Lager & Einheiten', icon: <Layers className="w-4 h-4" /> },
    { id: 'planning', label: 'Plankarte', icon: <Calendar className="w-4 h-4" /> },
    { id: 'construction', label: 'Konstruktion', icon: <Wrench className="w-4 h-4" /> },
    { id: 'documents', label: 'Dokumente', icon: <FileText className="w-4 h-4" /> }
  ];

  if (formData.partType === 'Eigenfertigungsteil') {
    editingTabs.splice(6, 0, { id: 'bom', label: 'Stückliste', icon: <List className="w-4 h-4" /> });
  }

  return (
    <div className="flex h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-sm border dark:border-gray-700 animate-fadeIn relative">
      
      {/* ==================== LINKE SEITENLEISTE (MENÜ) ==================== */}
      <div className="w-64 bg-gray-50 dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col z-10 shrink-0">
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-[#0F52BA]" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Artikelstamm</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-1">
          <button 
            onClick={() => { setActiveTab('overview'); setFeedback(null); }}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-bold transition-colors ${
              activeTab === 'overview' ? 'bg-[#0F52BA] text-white shadow-sm' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <List className="w-4 h-4" /> Artikelübersicht
          </button>
          
          <button 
            onClick={() => { setActiveTab('global_planning'); setFeedback(null); }}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-bold transition-colors ${
              activeTab === 'global_planning' ? 'bg-[#0F52BA] text-white shadow-sm' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <Calendar className="w-4 h-4" /> Globale Plankarte
          </button>

          {isEditingView && (
            <div className="mt-4 animate-fadeIn">
              
              {/* Sidebar Suchfeld für Artikelnummer */}
              <div className="px-3 mb-2 text-xs font-bold text-gray-400 uppercase tracking-wider border-b dark:border-gray-700 pb-1 flex justify-between items-center h-8">
                {isEditingMode ? (
                  <span>{currentId ? formData.articleId : 'Neuer Artikel'}</span>
                ) : (
                  <div className="flex items-center w-full relative">
                    <Search className="w-3 h-3 text-gray-400 absolute left-0" />
                    <input
                      type="text"
                      value={sidebarSearchId}
                      onChange={(e) => setSidebarSearchId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const found = articles.find(a => String(a.articleId).trim() === String(sidebarSearchId).trim());
                          if (found) {
                            openArticle(found);
                          } else {
                            setFeedback({ type: 'error', text: 'Artikelnummer nicht gefunden.' });
                            setTimeout(() => setFeedback(null), 3000);
                          }
                        }
                      }}
                      className="w-full bg-transparent border-none pl-5 py-1 text-xs focus:ring-0 outline-none text-gray-700 dark:text-gray-300 placeholder-gray-400 font-mono transition-colors"
                      placeholder="Suchen & Enter..."
                      title="Artikelnummer ändern und Enter drücken zum Suchen"
                    />
                  </div>
                )}
                {isEditingMode && <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">Bearbeitung</span>}
              </div>

              <div className="flex flex-col gap-1">
                {editingTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' 
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ==================== HAUPTBEREICH (CONTENT) ==================== */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-gray-900">
        
        {/* --- KOPFZEILE (Nur im Artikel-Kontext) --- */}
        {isEditingView && (
          <div className="flex justify-between items-center bg-white dark:bg-gray-800 p-4 border-b dark:border-gray-700 z-10 shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {currentId ? (isEditingMode ? `Bearbeiten: ${formData.name}` : formData.name) : 'Neuen Artikel anlegen'}
              </h2>
              {formData.status === 'ersetzt' && <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">Ersetzt</span>}
              {formData.status === 'inaktiv' && <span className="bg-gray-200 text-gray-700 text-xs font-bold px-2 py-1 rounded-full">Inaktiv</span>}
            </div>
            
            <div className="flex items-center gap-4">
               {feedback && (
                <div className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg ${feedback.type === 'error' ? 'text-red-700 bg-red-50' : 'text-green-700 bg-green-50'}`}>
                  {feedback.type === 'error' ? <AlertCircle className="w-4 h-4"/> : <CheckCircle2 className="w-4 h-4"/>}
                  {feedback.text}
                </div>
              )}
              
              {isEditingMode ? (
                 <>
                   <button onClick={() => {
                     if (currentId) {
                       const orig = articles.find(a => a.id === currentId);
                       if (orig) openArticle(orig);
                     } else {
                       setActiveTab('overview');
                     }
                   }} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors">
                     Abbrechen
                   </button>
                   <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-[#0F52BA] text-white rounded-lg font-bold flex items-center gap-2 shadow hover:bg-blue-800 transition-all disabled:opacity-50">
                     <Save className="w-4 h-4" /> {isSaving ? 'Speichert...' : 'Speichern'}
                   </button>
                 </>
              ) : (
                 <>
                   <button onClick={() => setActiveTab('overview')} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors">
                     Schließen
                   </button>
                   <button onClick={() => setIsEditingMode(true)} className="px-6 py-2 bg-[#0F52BA] text-white rounded-lg font-bold flex items-center gap-2 shadow hover:bg-blue-800 transition-all">
                     <Edit className="w-4 h-4" /> Bearbeiten
                   </button>
                 </>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6">
          <div className={`${isEditingView && activeTab !== 'calculation' ? 'max-w-4xl' : 'max-w-full'} mx-auto`}>

            {/* =========================================================
                ANSICHT: ÜBERSICHT / LISTE
            ========================================================= */}
            {activeTab === 'overview' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Artikelübersicht</h2>
                  <button onClick={handleAddNew} className="bg-[#0F52BA] hover:bg-blue-800 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-colors">
                    <Plus className="w-4 h-4"/> Neuer Artikel
                  </button>
                </div>

                {/* Suche & Filter */}
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border dark:border-gray-700 shadow-sm">
                  <div className="flex gap-3 max-w-2xl">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input type="text" placeholder="Suchen nach Nr, Name, Rubrik..." value={searchGeneral} onChange={e => setSearchGeneral(e.target.value)} className="w-full pl-9 pr-3 py-2 border dark:border-gray-600 rounded-lg text-sm bg-gray-50 dark:bg-gray-900 outline-none dark:text-white" />
                    </div>
                    <button 
                      onClick={() => setShowAdvancedSearch(!showAdvancedSearch)} 
                      className={`px-4 py-2 flex items-center gap-2 rounded-lg text-sm font-medium transition-colors ${showAdvancedSearch ? 'bg-[#0F52BA] text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}
                    >
                      <Filter className="w-4 h-4"/> Erweiterte Suche
                    </button>
                  </div>

                  {/* Erweiterte Suche Felder */}
                  {showAdvancedSearch && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t dark:border-gray-700 animate-fadeIn text-sm">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Materialart</label>
                        <select value={advancedSearch.materialType} onChange={e => setAdvancedSearch({...advancedSearch, materialType: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white">
                          <option value="">Alle</option><option value="Rohstoff">Rohstoff</option><option value="Hilfsstoff">Hilfsstoff</option><option value="Betriebsstoff">Betriebsstoff</option><option value="Fertigerzeugnis">Fertigerzeugnis</option><option value="Halbfabrikat">Halbfabrikat</option><option value="Handelsware">Handelsware</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Rubrik</label>
                        <input type="text" value={advancedSearch.category} onChange={e => setAdvancedSearch({...advancedSearch, category: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Hersteller</label>
                        <input type="text" value={advancedSearch.manufacturer} onChange={e => setAdvancedSearch({...advancedSearch, manufacturer: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Hersteller-Nr.</label>
                        <input type="text" value={advancedSearch.manufacturerPartId} onChange={e => setAdvancedSearch({...advancedSearch, manufacturerPartId: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Lieferant</label>
                        <input type="text" value={advancedSearch.supplier} onChange={e => setAdvancedSearch({...advancedSearch, supplier: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Lief.-Art-Nr.</label>
                        <input type="text" value={advancedSearch.supplierPartId} onChange={e => setAdvancedSearch({...advancedSearch, supplierPartId: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">DIN / Norm</label>
                        <input type="text" value={advancedSearch.dinStandard} onChange={e => setAdvancedSearch({...advancedSearch, dinStandard: e.target.value})} className="w-full p-2 border dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 dark:text-white" />
                      </div>
                      <div className="flex items-end pb-1">
                        <button onClick={resetAdvancedSearch} className="text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium">Filter zurücksetzen</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th className="p-4">Artikel</th>
                        <th className="p-4">Klassifizierung</th>
                        <th className="p-4">Zusatzinfos</th>
                        <th className="p-4 text-right">Bestand</th>
                        <th className="p-4 text-center">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {filtered.length === 0 ? (
                        <tr><td colSpan="5" className="p-8 text-center text-gray-500">Keine Artikel gefunden.</td></tr>
                      ) : (
                        filtered.map(a => {
                          const isReplaced = a.status === 'ersetzt';
                          return (
                          <tr key={a.id} className={`hover:bg-gray-50 dark:hover:bg-gray-750 group cursor-pointer ${isReplaced ? 'opacity-60 bg-gray-50 dark:bg-gray-800/30' : ''}`} onClick={() => openArticle(a)}>
                            <td className="p-4">
                              <div className="flex flex-col">
                                <span className={`font-bold text-gray-900 dark:text-white ${isReplaced ? 'line-through' : ''}`}>{a.name}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-[#0F52BA] font-mono font-bold">{a.articleId}</span>
                                  {a.category && <span className="text-[10px] text-gray-500">{a.category}</span>}
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                               <div className="flex flex-col gap-1 items-start">
                                 <span className="text-xs text-gray-700 dark:text-gray-300">{a.materialType} | {a.partType}</span>
                                 {a.usageCategory === 'Exot' && <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-bold">Exot</span>}
                               </div>
                            </td>
                            <td className="p-4 text-sm text-gray-500">
                               {a.manufacturer && <div>Herst: <span className="dark:text-gray-300">{a.manufacturer}</span></div>}
                               {a.dinStandard && <div>Norm: <span className="dark:text-gray-300">{a.dinStandard}</span></div>}
                               {(!a.manufacturer && !a.dinStandard) && '-'}
                            </td>
                            <td className="p-4 text-right">
                              <span className={`font-bold text-lg ${a.stock <= (a.minStock || 0) ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                {a.stock} <span className="text-xs font-normal text-gray-500">{a.storageUnit || a.unit}</span>
                              </span>
                            </td>
                            <td className="p-4 text-center">
                              <button onClick={(e) => { e.stopPropagation(); handleDelete(a.id, a.name); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        )})
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* =========================================================
                ANSICHT: GLOBALE PLANKARTE
            ========================================================= */}
            {activeTab === 'global_planning' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex items-center gap-3 border-b dark:border-gray-700 pb-4">
                  <Calendar className="w-6 h-6 text-[#0F52BA]" />
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Globale Plankarte</h2>
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border dark:border-gray-700 shadow-sm max-w-xl">
                  <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Artikelnummer eingeben zur Disposition:</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      placeholder="z.B. 100000" 
                      value={globalPlanningSearch} 
                      onChange={e => setGlobalPlanningSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleGlobalPlanningExecute()}
                      className="flex-1 p-2.5 border dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 outline-none dark:text-white font-mono text-lg"
                    />
                    <button 
                      onClick={handleGlobalPlanningExecute}
                      className="px-6 py-2 bg-[#0F52BA] text-white rounded-lg font-bold shadow-sm hover:bg-blue-800 transition-colors"
                    >
                      Prüfen
                    </button>
                  </div>
                  {feedback && activeTab === 'global_planning' && (
                    <div className="mt-2 text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4"/> {feedback.text}</div>
                  )}
                </div>

                {globalPlanningArticle && (
                  <div className="mt-8 animate-fadeIn">
                    <div className="mb-4 flex items-center gap-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{globalPlanningArticle.articleId} - {globalPlanningArticle.name}</h3>
                      <span className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-gray-600 dark:text-gray-300">{globalPlanningArticle.materialType}</span>
                    </div>

                    <div className="border dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                      <table className="w-full text-left text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                          <tr>
                            <th className="p-3">Datum</th>
                            <th className="p-3">Vorgang</th>
                            <th className="p-3">Referenz</th>
                            <th className="p-3 text-right text-green-600">Zugang</th>
                            <th className="p-3 text-right text-red-600">Abgang</th>
                            <th className="p-3 text-right bg-blue-50 dark:bg-blue-900/10">Gepl. Bestand</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-gray-700 bg-white dark:bg-gray-900">
                          <tr>
                            <td className="p-3 font-medium text-gray-900 dark:text-gray-100">Heute</td>
                            <td className="p-3 text-gray-500">Ist-Bestand</td>
                            <td className="p-3">-</td>
                            <td className="p-3 text-right">-</td>
                            <td className="p-3 text-right">-</td>
                            <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-white">{globalPlanningArticle.stock}</td>
                          </tr>
                          <tr className="opacity-60">
                            <td className="p-3 text-gray-500">In 3 Tagen</td>
                            <td className="p-3 text-gray-500">Kundenauftrag</td>
                            <td className="p-3 text-blue-600 hover:underline cursor-pointer">AUF-2026-084</td>
                            <td className="p-3 text-right">-</td>
                            <td className="p-3 text-right text-red-600">-15</td>
                            <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-gray-300">{globalPlanningArticle.stock - 15}</td>
                          </tr>
                          <tr className="opacity-60">
                            <td className="p-3 text-gray-500">In 7 Tagen</td>
                            <td className="p-3 text-gray-500">Bestellung (Einkauf)</td>
                            <td className="p-3 text-blue-600 hover:underline cursor-pointer">BES-2026-102</td>
                            <td className="p-3 text-right text-green-600">+50</td>
                            <td className="p-3 text-right">-</td>
                            <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-gray-300">{(globalPlanningArticle.stock - 15) + 50}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* =========================================================
                ANSICHT: ALLGEMEIN
            ========================================================= */}
            {activeTab === 'general' && (
              <div className="space-y-8 animate-fadeIn">
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 border-b dark:border-gray-700 pb-2">Stammdaten</h3>
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Artikelnummer *</label>
                      {!isEditingMode ? (
                        <div>
                          <input 
                            type="text" 
                            value={readOnlySearchId} 
                            onChange={e => setReadOnlySearchId(e.target.value)} 
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const found = articles.find(a => String(a.articleId).trim() === String(readOnlySearchId).trim());
                                if (found) {
                                  openArticle(found);
                                } else {
                                  setFeedback({ type: 'error', text: 'Artikelnummer nicht gefunden.' });
                                  setTimeout(() => setFeedback(null), 3000);
                                }
                              }
                            }}
                            className="w-full p-2.5 bg-gray-50 dark:bg-gray-800 border dark:border-gray-700/50 rounded-lg text-sm font-mono dark:text-white focus:border-blue-500 outline-none transition-colors shadow-inner" 
                            placeholder="Suchen & Enter..."
                          />
                          <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1"><Search className="w-3 h-3"/> Ändern und Enter drücken zum Suchen</div>
                        </div>
                      ) : (
                        <input 
                          type="text" 
                          name="articleId" 
                          value={formData.articleId} 
                          onChange={handleInputChange} 
                          disabled={currentId !== null}
                          className={`w-full p-2.5 rounded-lg text-sm font-mono border dark:border-gray-700 ${currentId !== null ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-white dark:bg-gray-800 dark:text-white focus:border-blue-500 outline-none'}`} 
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Teileart</label>
                      <select name="partType" disabled={!isEditingMode} value={formData.partType} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Einkaufsteil">Einkaufsteil</option><option value="Eigenfertigungsteil">Eigenfertigungsteil</option><option value="Variantenteil">Variantenteil</option><option value="Beistellteil">Beistellteil</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Bezeichnung *</label>
                      <input type="text" name="name" disabled={!isEditingMode} value={formData.name} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm font-bold dark:text-white text-lg disabled:bg-transparent disabled:border-transparent disabled:px-0" placeholder="Aussagekräftige Bezeichnung..." />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Ausführliche Beschreibung</label>
                      <textarea name="description" disabled={!isEditingMode} value={formData.description} onChange={handleInputChange} rows="3" className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:px-0 disabled:resize-none" placeholder="Zusätzliche Details, Spezifikationen..."></textarea>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Materialart</label>
                      <select name="materialType" disabled={!isEditingMode} value={formData.materialType} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Rohstoff">Rohstoff</option><option value="Hilfsstoff">Hilfsstoff</option><option value="Betriebsstoff">Betriebsstoff</option><option value="Fertigerzeugnis">Fertigerzeugnis</option><option value="Halbfabrikat">Halbfabrikat / Baugruppe</option><option value="Handelsware">Handelsware</option><option value="Dienstleistung">Dienstleistung</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rubrik / Warengruppe</label>
                      <input type="text" name="category" disabled={!isEditingMode} value={formData.category} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="z.B. Elektronik, Verbindungselemente..." />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hersteller</label>
                      <input type="text" name="manufacturer" disabled={!isEditingMode} value={formData.manufacturer || ''} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="Originalhersteller..." />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hersteller Artikelnummer</label>
                      <input type="text" name="manufacturerPartId" disabled={!isEditingMode} value={formData.manufacturerPartId || ''} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="Teilenummer beim Hersteller..." />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 border-b dark:border-gray-700 pb-2">Status & Lebenszyklus</h3>
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Verwendungskategorie</label>
                      <select name="usageCategory" disabled={!isEditingMode} value={formData.usageCategory} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Standard">Standardartikel (Regelbedarf)</option>
                        <option value="Exot">Sonderbeschaffung / Einmalbedarf (Exot)</option>
                      </select>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg border dark:border-gray-700">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Lebenszyklus Status</label>
                      <select name="status" disabled={!isEditingMode} value={formData.status} onChange={handleInputChange} className={`w-full p-2.5 border rounded-lg text-sm font-bold ${formData.status === 'ersetzt' ? 'text-red-600' : 'text-gray-900 dark:text-white'} ${!isEditingMode ? 'bg-transparent border-transparent px-0' : 'bg-white dark:bg-gray-800 dark:border-gray-700'}`}>
                        <option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv gesperrt</option><option value="ersetzt">Ersetzt durch Nachfolger</option>
                      </select>
                      
                      {formData.status === 'ersetzt' && formData.replacedBy && !isEditingMode && (
                        <div className="mt-3 animate-fadeIn">
                           <label className="block text-xs font-bold text-red-500 uppercase mb-1 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3"/> Nachfolger (Ersetzt durch)</label>
                           <div 
                              className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg cursor-pointer hover:underline font-bold text-sm inline-block"
                              onClick={() => {
                                 const succ = articles.find(a => a.articleId === formData.replacedBy);
                                 if (succ) openArticle(succ);
                              }}
                           >
                              {formData.replacedBy} {articles.find(a => a.articleId === formData.replacedBy)?.name ? `- ${articles.find(a => a.articleId === formData.replacedBy)?.name}` : ''}
                           </div>
                        </div>
                      )}
                      
                      {formData.status === 'ersetzt' && isEditingMode && (
                        <div className="mt-3 animate-fadeIn">
                          <label className="block text-xs font-bold text-red-500 uppercase mb-1 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3"/> Nachfolger (Ersetzt durch)</label>
                          <div className="relative">
                            <input 
                              type="text" 
                              value={replacedBySearchTerm || formData.replacedBy}
                              onChange={e => {
                                setReplacedBySearchTerm(e.target.value);
                                setFormData(prev => ({ ...prev, replacedBy: e.target.value }));
                                setShowReplacedByDropdown(true);
                              }}
                              onFocus={() => setShowReplacedByDropdown(true)}
                              onBlur={() => setTimeout(() => setShowReplacedByDropdown(false), 200)}
                              placeholder="Artikel suchen..."
                              className="w-full p-2 bg-white dark:bg-gray-800 border-red-200 rounded-lg text-sm dark:text-white"
                            />
                            {showReplacedByDropdown && (
                              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                 {articles
                                   .filter(a => a.articleId !== formData.articleId)
                                   .filter(a => {
                                     const term = (replacedBySearchTerm || '').toLowerCase();
                                     return a.articleId.toLowerCase().includes(term) || a.name.toLowerCase().includes(term);
                                   })
                                   .map(a => (
                                     <div 
                                       key={a.id}
                                       className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
                                       onMouseDown={(e) => { 
                                         e.preventDefault(); 
                                         setFormData(prev => ({ ...prev, replacedBy: a.articleId }));
                                         setReplacedBySearchTerm(`${a.articleId} - ${a.name}`);
                                         setShowReplacedByDropdown(false);
                                       }}
                                     >
                                       <span className="font-bold">{a.articleId}</span> - {a.name}
                                     </div>
                                 ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Anzeige Vorgänger falls existent */}
                    {(() => {
                       // Finde Artikel, die durch den aktuellen Artikel ersetzt wurden
                       const predecessors = articles.filter(a => a.replacedBy === formData.articleId || formData.replaces === a.articleId);
                       if (predecessors.length > 0) {
                         return (
                           <div className="col-span-2 mt-2 pt-4 border-t dark:border-gray-700 animate-fadeIn">
                              <label className="block text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1">Vorgänger Artikel</label>
                              <div className="flex flex-col gap-2">
                                {predecessors.map(p => (
                                  <div 
                                    key={p.id}
                                    onClick={() => openArticle(p)}
                                    className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer hover:underline text-sm flex items-center gap-2 inline-flex self-start"
                                  >
                                     <ArrowRightLeft className="w-3 h-3 text-gray-400"/>
                                     <span className="font-bold">{p.articleId}</span> - {p.name}
                                  </div>
                                ))}
                              </div>
                           </div>
                         );
                       }
                       return null;
                    })()}
                  </div>
                </section>
              </div>
            )}

            {/* =========================================================
                ANSICHT: DISPOSITION
            ========================================================= */}
            {activeTab === 'disposition' && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Activity className="w-5 h-5 text-[#0F52BA]"/> Planungs- und Dispositionsdaten</h3>
                <div className="grid grid-cols-2 gap-5 max-w-2xl">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dispositionsart</label>
                    <select name="dispoType" disabled={!isEditingMode} value={formData.dispoType} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                      <option value="Bedarfsgesteuert">Bedarfsgesteuert (Plangesteuert / MRP)</option><option value="Verbrauchsgesteuert">Verbrauchsgesteuert (Stochastisch)</option><option value="Auftragsbezogen">Auftragsbezogen (Make-to-Order)</option><option value="Keine Disposition">Keine Disposition (Manuell)</option>
                    </select>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dispositionsauslöser</label>
                    <select name="dispoTrigger" disabled={!isEditingMode} value={formData.dispoTrigger} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                      <option value="Meldebestand">Unterschreitung Meldebestand</option><option value="Kundenauftrag">Kundenauftragseingang</option><option value="Bestellpunkt">Bestellpunktverfahren</option><option value="Kanban">Kanban / Behälter leer</option>
                    </select>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800/30">
                    <label className="block text-xs font-bold text-blue-800 dark:text-blue-400 uppercase mb-1">Melde- / Mindestbestand</label>
                    <div className="flex items-center gap-2">
                      <input type="number" disabled={!isEditingMode} name="minStock" value={formData.minStock} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-700 rounded-lg text-lg font-bold text-blue-900 dark:text-blue-100 disabled:bg-transparent disabled:border-transparent disabled:px-0" />
                      <span className="text-gray-500">{formData.storageUnit || 'Stk'}</span>
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border dark:border-gray-700">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Wiederbeschaffungszeit (Tage)</label>
                    <input type="number" disabled={!isEditingMode} name="leadTime" value={formData.leadTime} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-lg dark:text-white disabled:bg-transparent disabled:border-transparent disabled:px-0" placeholder="z.B. 14" />
                  </div>
                </div>
              </div>
            )}

            {/* =========================================================
                ANSICHT: EINKAUF
            ========================================================= */}
            {activeTab === 'purchasing' && (
              <div className="space-y-8 animate-fadeIn">
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2">Allgemeine Einkaufsdaten</h3>
                  <div className="grid grid-cols-2 gap-5 max-w-xl">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Warentarifzollnummer (HS-Code)</label>
                      <input type="text" disabled={!isEditingMode} name="customsTariffNumber" value={formData.customsTariffNumber} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="z.B. 85423190" />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2">Zusatzfelder (EAN, Hersteller-Nr etc.)</h3>
                  
                  {isEditingMode && (
                    <div className="flex gap-2 mb-3">
                      <input type="text" placeholder="Feldname (z.B. EAN)" value={newCustomField.name} onChange={e => setNewCustomField({...newCustomField, name: e.target.value})} className="p-2 border rounded-lg text-sm flex-1 dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                      <input type="text" placeholder="Wert" value={newCustomField.value} onChange={e => setNewCustomField({...newCustomField, value: e.target.value})} className="p-2 border rounded-lg text-sm flex-1 dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                      <button onClick={() => addItemToList('customFields', newCustomField, setNewCustomField, {name: '', value: ''})} className="px-3 bg-gray-200 dark:bg-gray-700 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">Hinzufügen</button>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {(formData.customFields || []).map(cf => (
                      <div key={cf.id} className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 px-3 py-1.5 rounded-lg text-sm">
                        <span className="font-bold text-gray-500">{cf.name}:</span>
                        <span className="dark:text-white">{cf.value}</span>
                        {isEditingMode && (
                          <button onClick={() => removeItemFromList('customFields', cf.id)} className="ml-2 text-red-500 hover:text-red-700"><X className="w-3 h-3"/></button>
                        )}
                      </div>
                    ))}
                    {(!formData.customFields || formData.customFields.length === 0) && !isEditingMode && (
                      <span className="text-sm text-gray-500">Keine Zusatzfelder gepflegt.</span>
                    )}
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2">Lieferanten & Preise</h3>
                  
                  {isEditingMode && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 mb-4">
                      <div className="flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[200px] relative">
                          <label className="block text-xs text-gray-500 mb-1">Lieferant suchen (Name / Rubrik)</label>
                          <div className="flex gap-1">
                            <div className="relative flex-1">
                              <input 
                                type="text" 
                                value={supplierSearchTerm} 
                                onChange={e => {
                                  setSupplierSearchTerm(e.target.value);
                                  setShowSupplierDropdown(true);
                                  setNewSupplier({...newSupplier, supplierId: '', name: ''}); 
                                }}
                                onFocus={() => setShowSupplierDropdown(true)}
                                onBlur={() => setShowSupplierDropdown(false)}
                                className="w-full p-2 pl-3 pr-8 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white outline-none focus:border-blue-500"
                                placeholder="Name oder Rubrik tippen..."
                              />
                              {showSupplierDropdown && (
                                 <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                   {dbSuppliers.filter(s => {
                                     const term = String(supplierSearchTerm || '').toLowerCase();
                                     return String(s.name || '').toLowerCase().includes(term) || 
                                            String(s.category || '').toLowerCase().includes(term) ||
                                            String(s.rubrik || '').toLowerCase().includes(term);
                                   }).map(sup => (
                                      <div 
                                        key={sup.id} 
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm flex flex-col"
                                        onMouseDown={(e) => { e.preventDefault(); handleSelectSupplier(sup); }}
                                      >
                                        <span className="font-bold text-gray-900 dark:text-white">{sup.name}</span>
                                        {(sup.category || sup.rubrik) && <span className="text-[10px] text-gray-500">{sup.category || sup.rubrik}</span>}
                                      </div>
                                   ))}
                                 </div>
                              )}
                            </div>
                            <button 
                              onClick={(e) => { e.preventDefault(); setShowSupplierModal(true); }}
                              className="p-2 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500"
                              title="Erweiterte Lieferantensuche"
                            >
                              <Search className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 min-w-[120px]">
                          <label className="block text-xs text-gray-500 mb-1">Lief.-Art-Nr</label>
                          <input type="text" value={newSupplier.partId} onChange={e => setNewSupplier({...newSupplier, partId: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white"/>
                        </div>
                        <div className="w-24">
                          <label className="block text-xs text-gray-500 mb-1">Preis</label>
                          <input type="number" step="0.01" value={newSupplier.price} onChange={e => setNewSupplier({...newSupplier, price: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                        </div>
                        <div className="w-24">
                          <label className="block text-xs text-gray-500 mb-1">Lieferzeit (Tage)</label>
                          <input type="number" min="0" value={newSupplier.leadTime} onChange={e => setNewSupplier({...newSupplier, leadTime: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                        </div>
                        <button onClick={() => {
                          if(!newSupplier.supplierId || !newSupplier.name) return;
                          setFormData(prev => ({
                            ...prev, suppliers: [...(prev.suppliers || []), { ...newSupplier, id: Date.now().toString(), isMain: (prev.suppliers || []).length === 0 }]
                          }));
                          setNewSupplier({ supplierId: '', name: '', partId: '', price: '', currency: 'EUR', leadTime: '' });
                          setSupplierSearchTerm(''); 
                        }} className="px-4 py-2 bg-gray-800 text-white dark:bg-gray-700 rounded-lg hover:bg-gray-900 text-sm font-medium">Add</button>
                      </div>
                    </div>
                  )}

                  <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="p-3 w-12 text-center">Haupt</th>
                          <th className="p-3">Lieferant</th>
                          <th className="p-3">Art-Nr.</th>
                          <th className="p-3 text-right">Lieferzeit</th>
                          <th className="p-3 text-right">Preis</th>
                          {isEditingMode && <th className="p-3 text-center">Entf.</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {(!formData.suppliers || formData.suppliers.length === 0) && (
                          <tr><td colSpan={isEditingMode ? 6 : 5} className="p-4 text-center text-sm text-gray-500">Keine Lieferanten.</td></tr>
                        )}
                        {(formData.suppliers || []).map(sup => (
                          <tr key={sup.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 text-center">
                              <button disabled={!isEditingMode} onClick={() => {
                                setFormData(prev => ({
                                  ...prev, suppliers: prev.suppliers.map(s => ({ ...s, isMain: s.id === sup.id }))
                                }));
                              }} className={`transition-colors ${sup.isMain ? 'text-yellow-500' : 'text-gray-300'} ${isEditingMode ? 'hover:text-yellow-300' : 'cursor-default'}`}>
                                <Star className="w-5 h-5 fill-current" />
                              </button>
                            </td>
                            <td className="p-3 font-medium dark:text-white">{sup.name}</td>
                            <td className="p-3 text-sm font-mono text-gray-600 dark:text-gray-400">{sup.partId || '-'}</td>
                            <td className="p-3 text-right text-gray-600 dark:text-gray-400">{sup.leadTime ? `${sup.leadTime} Tage` : '-'}</td>
                            <td className="p-3 text-right font-medium dark:text-white">{sup.price ? `${parseNum(sup.price).toFixed(2)} ${sup.currency}` : '-'}</td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => removeItemFromList('suppliers', sup.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}

            {/* =========================================================
                ANSICHT: KALKULATION (MAKE OR BUY)
            ========================================================= */}
            {activeTab === 'calculation' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex justify-between items-end border-b dark:border-gray-700 pb-2">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-[#0F52BA]" /> Preiskalkulation (Make-or-Buy)
                  </h3>
                  <span className="text-sm text-gray-500">Standard-VK aktuell: <strong>{Number(formData.salesPrice || 0).toFixed(2)} EUR</strong></span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
                  
                  {/* --- SPALTE 1: MAKE (Eigenfertigung) --- */}
                  <div className={`bg-white dark:bg-gray-800 rounded-xl border ${formData.partType === 'Eigenfertigungsteil' ? 'border-[#0F52BA] shadow-md ring-1 ring-[#0F52BA]/20' : 'border-gray-200 dark:border-gray-700'} overflow-hidden`}>
                    <div className={`px-4 py-3 border-b flex items-center gap-2 ${formData.partType === 'Eigenfertigungsteil' ? 'bg-blue-50 dark:bg-blue-900/30 border-[#0F52BA]/20' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}`}>
                      <Factory className={`w-5 h-5 ${formData.partType === 'Eigenfertigungsteil' ? 'text-[#0F52BA]' : 'text-gray-500'}`}/>
                      <h4 className={`font-bold ${formData.partType === 'Eigenfertigungsteil' ? 'text-blue-900 dark:text-blue-100' : 'text-gray-700 dark:text-gray-300'}`}>
                        Zuschlagskalkulation (Make)
                      </h4>
                    </div>
                    
                    <div className="p-5 space-y-4">
                      {/* Materialkosten */}
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Material (aus Stückliste)</span>
                        <span className="font-mono font-bold dark:text-white">{getBomCost().toFixed(2)} EUR</span>
                      </div>
                      
                      {/* Fertigungslöhne / Kosten */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Fertigungskosten (Manuell in EUR)</label>
                        <input 
                          type="text" 
                          disabled={!isEditingMode}
                          name="productionCosts" 
                          value={formData.calculation?.make?.productionCosts ?? ''} 
                          onChange={handleCalculationMakeChange} 
                          placeholder="0.00"
                          className="w-full p-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-right font-mono dark:text-white disabled:bg-transparent disabled:border-transparent" 
                        />
                      </div>

                      {/* Gemeinkosten */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Gemeinkosten (Absolut in EUR)</label>
                        <input 
                          type="text" 
                          disabled={!isEditingMode}
                          name="overhead" 
                          value={formData.calculation?.make?.overhead ?? ''} 
                          onChange={handleCalculationMakeChange} 
                          placeholder="0.00"
                          className="w-full p-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-right font-mono dark:text-white disabled:bg-transparent disabled:border-transparent" 
                        />
                      </div>

                      {/* Selbstkosten */}
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center text-sm">
                        <span className="font-bold text-gray-700 dark:text-gray-300">Selbstkosten</span>
                        <span className="font-mono font-bold dark:text-white">
                          {(getBomCost() + parseNum(formData.calculation?.make?.productionCosts) + parseNum(formData.calculation?.make?.overhead)).toFixed(2)} EUR
                        </span>
                      </div>

                      {/* Gewinn */}
                      <div className="pt-3">
                        <label className="block text-xs font-bold text-[#0F52BA] mb-1">Gewinnzuschlag (%)</label>
                        <div className="flex items-center gap-2">
                          <input 
                            type="text" 
                            disabled={!isEditingMode}
                            name="markup" 
                            value={formData.calculation?.make?.markup ?? '20'} 
                            onChange={handleCalculationMakeChange} 
                            placeholder="20.0"
                            className="w-full p-2 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-right font-bold font-mono dark:bg-gray-800 dark:text-white disabled:bg-transparent disabled:border-transparent" 
                          />
                          <span className="text-blue-800 dark:text-blue-400 font-bold">%</span>
                        </div>
                      </div>

                      {/* Ergebnis */}
                      <div className="pt-4 mt-2 border-t-2 border-[#0F52BA]/20 flex justify-between items-center">
                        <span className="font-bold text-blue-900 dark:text-blue-100">Kalkulierter Preis</span>
                        <span className="text-xl font-black text-[#0F52BA] dark:text-blue-400">
                          {((getBomCost() + parseNum(formData.calculation?.make?.productionCosts) + parseNum(formData.calculation?.make?.overhead)) * (1 + parseNum(formData.calculation?.make?.markup) / 100)).toFixed(2)} EUR
                        </span>
                      </div>

                      {isEditingMode && (
                        <button 
                          onClick={() => handleApplySalesPrice(((getBomCost() + parseNum(formData.calculation?.make?.productionCosts) + parseNum(formData.calculation?.make?.overhead)) * (1 + parseNum(formData.calculation?.make?.markup) / 100)))}
                          className="w-full mt-4 py-2.5 bg-gray-100 hover:bg-[#0F52BA] hover:text-white text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-[#0F52BA] rounded-lg text-sm font-bold shadow-sm transition-colors flex justify-center items-center gap-2"
                        >
                          Diesen Preis übernehmen <ArrowRight className="w-4 h-4"/>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* --- SPALTE 2: BUY (Einkauf / Handel) --- */}
                  <div className={`bg-white dark:bg-gray-800 rounded-xl border ${formData.partType === 'Einkaufsteil' ? 'border-green-500 shadow-md ring-1 ring-green-500/20' : 'border-gray-200 dark:border-gray-700'} overflow-hidden`}>
                    <div className={`px-4 py-3 border-b flex items-center gap-2 ${formData.partType === 'Einkaufsteil' ? 'bg-green-50 dark:bg-green-900/20 border-green-500/20' : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'}`}>
                      <ShoppingCart className={`w-5 h-5 ${formData.partType === 'Einkaufsteil' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}/>
                      <h4 className={`font-bold ${formData.partType === 'Einkaufsteil' ? 'text-green-800 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        Handelskalkulation (Buy)
                      </h4>
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Basis Einkaufspreis */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1 flex justify-between">
                          <span>Einkaufspreis (Basis)</span>
                          <span className="text-[10px] text-gray-400 font-normal">Standard: Hauptlieferant</span>
                        </label>
                        <input 
                          type="text" 
                          disabled={!isEditingMode}
                          name="basePrice" 
                          value={formData.calculation?.buy?.basePrice ?? ''} 
                          onChange={handleCalculationBuyChange} 
                          placeholder={getSupplierCost() > 0 ? getSupplierCost().toFixed(2) : "0.00"}
                          className="w-full p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-right font-mono font-bold dark:text-white disabled:bg-transparent disabled:border-transparent" 
                        />
                        <div className="text-[10px] text-gray-400 mt-1">
                           Preis vom Lieferanten: {getSupplierCost().toFixed(2)} EUR (überschreiben möglich)
                        </div>
                      </div>

                      {/* Bezugs-/Gemeinkosten */}
                      <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">Bezugs-/Gemeinkosten (Absolut in EUR)</label>
                        <input 
                          type="text" 
                          disabled={!isEditingMode}
                          name="overhead" 
                          value={formData.calculation?.buy?.overhead ?? ''} 
                          onChange={handleCalculationBuyChange} 
                          placeholder="0.00"
                          className="w-full p-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-right font-mono dark:text-white disabled:bg-transparent disabled:border-transparent" 
                        />
                      </div>

                      {/* Selbstkosten */}
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center text-sm">
                        <span className="font-bold text-gray-700 dark:text-gray-300">Selbstkosten</span>
                        <span className="font-mono font-bold dark:text-white">
                          {((formData.calculation?.buy?.basePrice !== undefined && formData.calculation?.buy?.basePrice !== '' ? parseNum(formData.calculation?.buy?.basePrice) : getSupplierCost()) + parseNum(formData.calculation?.buy?.overhead)).toFixed(2)} EUR
                        </span>
                      </div>

                      {/* Gewinn */}
                      <div className="pt-3">
                        <label className="block text-xs font-bold text-green-700 dark:text-green-500 mb-1">Gewinnzuschlag (%)</label>
                        <div className="flex items-center gap-2">
                          <input 
                            type="text" 
                            disabled={!isEditingMode}
                            name="markup" 
                            value={formData.calculation?.buy?.markup ?? '20'} 
                            onChange={handleCalculationBuyChange} 
                            placeholder="20.0"
                            className="w-full p-2 border border-green-200 dark:border-green-800/50 rounded-lg text-sm text-right font-bold font-mono dark:bg-gray-800 dark:text-white disabled:bg-transparent disabled:border-transparent" 
                          />
                          <span className="text-green-700 dark:text-green-500 font-bold">%</span>
                        </div>
                      </div>

                      {/* Ergebnis */}
                      <div className="pt-4 mt-2 border-t-2 border-green-500/20 flex justify-between items-center">
                        <span className="font-bold text-green-900 dark:text-green-100">Kalkulierter Preis</span>
                        <span className="text-xl font-black text-green-600 dark:text-green-400">
                          {(((formData.calculation?.buy?.basePrice !== undefined && formData.calculation?.buy?.basePrice !== '' ? parseNum(formData.calculation?.buy?.basePrice) : getSupplierCost()) + parseNum(formData.calculation?.buy?.overhead)) * (1 + parseNum(formData.calculation?.buy?.markup) / 100)).toFixed(2)} EUR
                        </span>
                      </div>

                      {isEditingMode && (
                        <button 
                          onClick={() => handleApplySalesPrice((((formData.calculation?.buy?.basePrice !== undefined && formData.calculation?.buy?.basePrice !== '' ? parseNum(formData.calculation?.buy?.basePrice) : getSupplierCost()) + parseNum(formData.calculation?.buy?.overhead)) * (1 + parseNum(formData.calculation?.buy?.markup) / 100)))}
                          className="w-full mt-4 py-2.5 bg-gray-100 hover:bg-green-600 hover:text-white text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-green-600 rounded-lg text-sm font-bold shadow-sm transition-colors flex justify-center items-center gap-2"
                        >
                          Diesen Preis übernehmen <ArrowRight className="w-4 h-4"/>
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* =========================================================
                ANSICHT: VERKAUF
            ========================================================= */}
            {activeTab === 'sales' && (
              <div className="space-y-8 animate-fadeIn">
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-[#0F52BA]"/> Allgemeine Verkaufsdaten</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Standard Verkaufspreis (Netto)</label>
                      <div className="flex items-center gap-2">
                        <input type="number" disabled={!isEditingMode} step="0.01" name="salesPrice" value={formData.salesPrice} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-lg font-bold text-gray-900 dark:text-white disabled:bg-transparent disabled:border-transparent disabled:px-0" placeholder="0.00" />
                        <span className="text-gray-500 font-bold">EUR</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Steuerklasse / USt.</label>
                      <select name="taxRate" disabled={!isEditingMode} value={formData.taxRate} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="19">19% (Voller Steuersatz)</option>
                        <option value="7">7% (Ermäßigter Steuersatz)</option>
                        <option value="0">0% (Steuerfrei)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Warentarifzollnummer (HS-Code)</label>
                      <input type="text" disabled={!isEditingMode} name="customsTariffNumber" value={formData.customsTariffNumber} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="z.B. 85423190" />
                    </div>
                  </div>
                </section>

                {/* Sektion: Staffelpreise */}
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Layers className="w-5 h-5 text-[#0F52BA]"/> Staffelpreise (Allgemein)</h3>
                  
                  {isEditingMode && (
                    <div className="flex gap-3 mb-4 items-end bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Ab Menge ({formData.salesUnit || 'Stk'})</label>
                        <input 
                          type="number" 
                          min="1" 
                          value={newTierPrice.quantity} 
                          onChange={e => {
                            const val = e.target.value;
                            setNewTierPrice({...newTierPrice, quantity: val});
                          }} 
                          className="w-32 p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Netto-Preis</label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="0.01" value={newTierPrice.price} onChange={e => setNewTierPrice({...newTierPrice, price: e.target.value})} className="w-32 p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="0.00" />
                          <span className="text-sm text-gray-500">EUR</span>
                        </div>
                      </div>
                      <button onClick={() => {
                        const qty = parseInt(newTierPrice.quantity, 10);
                        if (!newTierPrice.price || isNaN(qty) || qty <= 0) return;
                        addItemToList('tieredPrices', { ...newTierPrice, quantity: qty }, setNewTierPrice, { quantity: '', price: '' });
                      }} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors">
                        Staffel hinzufügen
                      </button>
                    </div>
                  )}

                  <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="p-3 w-1/3">Ab Menge</th>
                          <th className="p-3 w-1/3 text-right">Netto-Preis</th>
                          {isEditingMode && <th className="p-3 text-center w-16">Entf.</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {(!formData.tieredPrices || formData.tieredPrices.length === 0) && (
                          <tr><td colSpan={isEditingMode ? 3 : 2} className="p-4 text-center text-sm text-gray-500">Keine Staffelpreise definiert. Es gilt der Standardpreis.</td></tr>
                        )}
                        {(formData.tieredPrices || []).sort((a,b) => a.quantity - b.quantity).map(tier => (
                          <tr key={tier.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 font-medium dark:text-white">ab {tier.quantity} <span className="text-xs text-gray-500 font-normal">{formData.salesUnit || 'Stk'}</span></td>
                            <td className="p-3 text-right font-bold text-blue-600 dark:text-blue-400">{parseNum(tier.price).toFixed(2)} EUR</td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => removeItemFromList('tieredPrices', tier.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4 mx-auto"/></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Sektion: Länderspezifische Preise */}
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Globe className="w-5 h-5 text-[#0F52BA]"/> Regionale / Länderspezifische Preise</h3>
                  
                  {isEditingMode && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 mb-4 flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs text-gray-500 mb-1">Region oder Land auswählen</label>
                        <select 
                          value={newRegionalPrice.region} 
                          onChange={e => setNewRegionalPrice({...newRegionalPrice, region: e.target.value})} 
                          className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                        >
                          <option value="Europa (EU)">Europa (EU)</option>
                          <option value="Europa (Nicht-EU)">Europa (Nicht-EU)</option>
                          <option value="DACH">DACH-Region</option>
                          <option value="Nordamerika">Nordamerika</option>
                          <option value="Asien">Asien</option>
                          <option value="Weltweit">Weltweit (Rest)</option>
                          <option value="Spezifisches Land">-- Spezifisches Land eingeben --</option>
                        </select>
                        {newRegionalPrice.region === 'Spezifisches Land' && (
                          <input 
                            type="text" 
                            placeholder="Land z.B. Schweiz" 
                            value={newRegionalPrice.customRegion} 
                            onChange={e => setNewRegionalPrice({...newRegionalPrice, customRegion: e.target.value})} 
                            className="w-full p-2 mt-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                          />
                        )}
                      </div>
                      
                      <div className="w-32">
                        <label className="block text-xs text-gray-500 mb-1">Preis</label>
                        <div className="flex items-center gap-2">
                           <input type="number" step="0.01" value={newRegionalPrice.price} onChange={e => setNewRegionalPrice({...newRegionalPrice, price: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="0.00" />
                        </div>
                      </div>
                      
                      <div className="w-24">
                        <label className="block text-xs text-gray-500 mb-1">Währung</label>
                        <select value={newRegionalPrice.currency} onChange={e => setNewRegionalPrice({...newRegionalPrice, currency: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                          <option value="CHF">CHF</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>

                      <button onClick={() => {
                        if(!newRegionalPrice.price) return;
                        const finalRegion = newRegionalPrice.region === 'Spezifisches Land' ? newRegionalPrice.customRegion : newRegionalPrice.region;
                        if(!finalRegion) return;
                        
                        addItemToList('regionalPrices', { ...newRegionalPrice, region: finalRegion }, setNewRegionalPrice, { region: 'Europa (EU)', customRegion: '', price: '', currency: 'EUR' });
                      }} className="px-4 py-2 bg-gray-800 text-white dark:bg-gray-700 rounded-lg hover:bg-gray-900 text-sm font-medium">Add</button>
                    </div>
                  )}

                  <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="p-3">Region / Land</th>
                          <th className="p-3 text-right">Lokaler Preis</th>
                          {isEditingMode && <th className="p-3 text-center">Entf.</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {(!formData.regionalPrices || formData.regionalPrices.length === 0) && (
                          <tr><td colSpan={isEditingMode ? 3 : 2} className="p-4 text-center text-sm text-gray-500">Keine regionalen Preise gepflegt.</td></tr>
                        )}
                        {(formData.regionalPrices || []).map(rp => (
                          <tr key={rp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 font-bold dark:text-white">{rp.region}</td>
                            <td className="p-3 text-right font-medium dark:text-white">{rp.price ? `${parseNum(rp.price).toFixed(2)} ${rp.currency}` : '-'}</td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => removeItemFromList('regionalPrices', rp.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Sektion: Kundenspezifische Artikel */}
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Users className="w-5 h-5 text-[#0F52BA]"/> Kundenspezifische Verknüpfungen</h3>
                  
                  {isEditingMode && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700 mb-4 flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs text-gray-500 mb-1">Kundenname / ID</label>
                        <input type="text" value={newCustomer.name} onChange={e => setNewCustomer({...newCustomer, name: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="z.B. Musterkunde GmbH" />
                      </div>
                      <div className="flex-1 min-w-[120px]">
                        <label className="block text-xs text-gray-500 mb-1">Kunden-Art.-Nr.</label>
                        <input type="text" value={newCustomer.partId} onChange={e => setNewCustomer({...newCustomer, partId: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="Nr. beim Kunden" />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs text-gray-500 mb-1">Sonderpreis</label>
                        <div className="flex items-center gap-2">
                           <input type="number" step="0.01" value={newCustomer.price} onChange={e => setNewCustomer({...newCustomer, price: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="0.00" />
                        </div>
                      </div>
                      <button onClick={() => {
                        if(!newCustomer.name) return;
                        addItemToList('customers', newCustomer, setNewCustomer, { name: '', partId: '', price: '', currency: 'EUR' });
                      }} className="px-4 py-2 bg-gray-800 text-white dark:bg-gray-700 rounded-lg hover:bg-gray-900 text-sm font-medium">Add</button>
                    </div>
                  )}

                  <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                    <table className="w-full text-left">
                      <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                        <tr>
                          <th className="p-3">Kunde</th>
                          <th className="p-3">Kunden-Art-Nr.</th>
                          <th className="p-3 text-right">Sonderpreis</th>
                          {isEditingMode && <th className="p-3 text-center">Entf.</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {(!formData.customers || formData.customers.length === 0) && (
                          <tr><td colSpan={isEditingMode ? 4 : 3} className="p-4 text-center text-sm text-gray-500">Keine kundenspezifischen Verknüpfungen vorhanden.</td></tr>
                        )}
                        {(formData.customers || []).map(cust => (
                          <tr key={cust.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 font-bold dark:text-white">{cust.name}</td>
                            <td className="p-3 text-sm font-mono text-gray-600 dark:text-gray-400">{cust.partId || '-'}</td>
                            <td className="p-3 text-right font-medium dark:text-white">{cust.price ? `${parseNum(cust.price).toFixed(2)} ${cust.currency}` : '-'}</td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => removeItemFromList('customers', cust.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}

            {/* =========================================================
                ANSICHT: LAGER & EINHEITEN
            ========================================================= */}
            {activeTab === 'inventory' && (
              <div className="space-y-8 animate-fadeIn">
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2">Mengen-Einheiten</h3>
                  <div className="grid grid-cols-3 gap-4 max-w-3xl">
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border dark:border-gray-700">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2"><Truck className="inline w-3 h-3 mr-1"/> Einkaufseinheit</label>
                      <select name="purchasingUnit" disabled={!isEditingMode} value={formData.purchasingUnit} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Stk">Stück (Stk)</option><option value="kg">Kilogramm (kg)</option><option value="m">Meter (m)</option><option value="Pack">Packung</option><option value="Pal">Palette</option>
                      </select>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30">
                      <label className="block text-xs font-bold text-blue-800 dark:text-blue-300 uppercase mb-2"><Layers className="inline w-3 h-3 mr-1"/> Lagereinheit (Basis)</label>
                      <select name="storageUnit" disabled={!isEditingMode} value={formData.storageUnit} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-800 border-blue-200 dark:border-blue-700 rounded text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Stk">Stück (Stk)</option><option value="kg">Kilogramm (kg)</option><option value="m">Meter (m)</option><option value="l">Liter (l)</option>
                      </select>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border dark:border-gray-700">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-2"><ShoppingCart className="inline w-3 h-3 mr-1"/> Verkaufseinheit</label>
                      <select name="salesUnit" disabled={!isEditingMode} value={formData.salesUnit} onChange={handleInputChange} className="w-full p-2 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0">
                        <option value="Stk">Stück (Stk)</option><option value="kg">Kilogramm (kg)</option><option value="m">Meter (m)</option><option value="Set">Set</option><option value="Karton">Karton</option>
                      </select>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2">Lagerorte & Bestände</h3>
                  
                  {isEditingMode && (
                    <div className="flex gap-4 items-end mb-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Lager / Halle</label>
                        <input type="text" value={newLocation.location} onChange={e => setNewLocation({...newLocation, location: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="z.B. Hauptlager" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Lagerplatz / Regal</label>
                        <input type="text" value={newLocation.bin} onChange={e => setNewLocation({...newLocation, bin: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" placeholder="z.B. A-01-05" />
                      </div>
                      <div className="w-32">
                        <label className="block text-xs text-gray-500 mb-1">Bestand</label>
                        <input type="number" value={newLocation.quantity} onChange={e => setNewLocation({...newLocation, quantity: Number(e.target.value)})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                      </div>
                      <button onClick={() => {
                          if(!newLocation.location) return;
                          setFormData(prev => {
                            const newLocations = [...(prev.storageLocations || []), { ...newLocation, id: Date.now().toString() }];
                            const newTotal = newLocations.reduce((sum, loc) => sum + Number(loc.quantity), 0);
                            return { ...prev, storageLocations: newLocations, stock: newTotal };
                          });
                          setNewLocation({ location: '', bin: '', quantity: 0 });
                        }} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium">Hinzufügen</button>
                    </div>
                  )}

                  <table className="w-full text-left border dark:border-gray-700 rounded-xl overflow-hidden">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="p-3"><MapPin className="w-3 h-3 inline mr-1"/> Lager</th>
                        <th className="p-3">Lagerplatz</th>
                        <th className="p-3 text-right">Menge</th>
                        {isEditingMode && <th className="p-3 text-center">Aktion</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {(!formData.storageLocations || formData.storageLocations.length === 0) ? (
                        <tr><td colSpan={isEditingMode ? 4 : 3} className="p-4 text-center text-sm text-gray-500">Keine spezifischen Lagerorte definiert. Bitte manuell den Gesamtbestand pflegen oder Lagerorte hinzufügen.</td></tr>
                      ) : (
                        formData.storageLocations.map(loc => (
                          <tr key={loc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 font-medium dark:text-white">{loc.location}</td>
                            <td className="p-3 text-gray-600 dark:text-gray-300">{loc.bin || '-'}</td>
                            <td className="p-3 text-right font-bold dark:text-white">{loc.quantity}</td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => {
                                  setFormData(prev => {
                                    const newLocations = prev.storageLocations.filter(l => l.id !== loc.id);
                                    const newTotal = newLocations.reduce((sum, l) => sum + Number(l.quantity), 0);
                                    return { ...prev, storageLocations: newLocations, stock: newTotal };
                                  });
                                }} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4 mx-auto"/></button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                      <tr className="bg-blue-50 dark:bg-blue-900/20 font-bold text-blue-900 dark:text-blue-100 border-t-2 border-blue-200 dark:border-blue-800">
                        <td colSpan="2" className="p-3 text-right uppercase text-xs">Gesamtbestand:</td>
                        <td className="p-3 text-right">
                          {/* Wenn keine Location existiert, erlaube manuelle Eingabe, sonst Summe */}
                          {(!formData.storageLocations || formData.storageLocations.length === 0) ? (
                            <input type="number" disabled={!isEditingMode} name="stock" value={formData.stock} onChange={handleInputChange} className="w-20 p-1 border rounded text-right bg-white dark:bg-gray-800 dark:text-white dark:border-gray-600 disabled:bg-transparent disabled:border-transparent disabled:px-0" />
                          ) : (
                            formData.stock
                          )}
                           <span className="text-xs font-normal ml-1 text-gray-500">{formData.storageUnit}</span>
                        </td>
                        {isEditingMode && <td></td>}
                      </tr>
                    </tbody>
                  </table>
                </section>
              </div>
            )}

            {/* =========================================================
                ANSICHT: PLANKARTE (Artikel-Kontext)
            ========================================================= */}
            {activeTab === 'planning' && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#0F52BA]"/> Plankarte (Vorschau)</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Diese Ansicht projiziert zukünftige Bestände basierend auf Disposition, offenen Bestellungen und Kundenaufträgen.</p>
                
                <div className="border dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="p-3">Datum</th>
                        <th className="p-3">Vorgang</th>
                        <th className="p-3">Referenz</th>
                        <th className="p-3 text-right text-green-600">Zugang</th>
                        <th className="p-3 text-right text-red-600">Abgang</th>
                        <th className="p-3 text-right bg-blue-50 dark:bg-blue-900/10">Gepl. Bestand</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700 bg-white dark:bg-gray-900">
                      <tr>
                        <td className="p-3 font-medium text-gray-900 dark:text-gray-100">Heute</td>
                        <td className="p-3 text-gray-500">Ist-Bestand</td>
                        <td className="p-3">-</td>
                        <td className="p-3 text-right">-</td>
                        <td className="p-3 text-right">-</td>
                        <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-white">{formData.stock}</td>
                      </tr>
                      {/* Statische Mock-Daten für die Visualisierung */}
                      <tr className="opacity-60">
                        <td className="p-3 text-gray-500">In 3 Tagen</td>
                        <td className="p-3 text-gray-500">Kundenauftrag</td>
                        <td className="p-3 text-blue-600 hover:underline cursor-pointer">AUF-2026-084</td>
                        <td className="p-3 text-right">-</td>
                        <td className="p-3 text-right text-red-600">-15</td>
                        <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-gray-300">{formData.stock - 15}</td>
                      </tr>
                      <tr className="opacity-60">
                        <td className="p-3 text-gray-500">In 7 Tagen</td>
                        <td className="p-3 text-gray-500">Bestellung (Einkauf)</td>
                        <td className="p-3 text-blue-600 hover:underline cursor-pointer">BES-2026-102</td>
                        <td className="p-3 text-right text-green-600">+50</td>
                        <td className="p-3 text-right">-</td>
                        <td className="p-3 text-right font-bold bg-blue-50 dark:bg-blue-900/10 dark:text-gray-300">{(formData.stock - 15) + 50}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* =========================================================
                ANSICHT: KONSTRUKTION
            ========================================================= */}
            {activeTab === 'construction' && (
              <div className="space-y-8 animate-fadeIn">
                <section>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><Wrench className="w-5 h-5 text-[#0F52BA]"/> Konstruktionsdaten</h3>
                  <div className="grid grid-cols-2 gap-5 max-w-2xl">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Zeichnungsnummer</label>
                      <input type="text" disabled={!isEditingMode} name="drawingNumber" value={formData.construction?.drawingNumber || ''} onChange={handleConstructionChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">DIN / Norm (z.B. DIN 912)</label>
                      <input type="text" disabled={!isEditingMode} name="dinStandard" value={formData.dinStandard || ''} onChange={handleInputChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="Norm eingeben..." />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Spezifisches Material / Werkstoff</label>
                      <input type="text" disabled={!isEditingMode} name="material" value={formData.construction?.material || ''} onChange={handleConstructionChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="z.B. S235JR, Alu 7075" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Farbe / Finish</label>
                      <input type="text" disabled={!isEditingMode} name="color" value={formData.construction?.color || ''} onChange={handleConstructionChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="z.B. RAL 9005, eloxiert" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Gewicht (in kg)</label>
                      <input type="number" disabled={!isEditingMode} step="0.01" name="weight" value={formData.construction?.weight || ''} onChange={handleConstructionChange} className="w-full p-2.5 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" />
                    </div>
                    
                    {/* Separierte Abmessungen L/B/H */}
                    <div className="col-span-2 grid grid-cols-3 gap-3 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700">
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Länge (mm)</label>
                        <input type="number" step="0.01" disabled={!isEditingMode} name="length" value={formData.construction?.length || ''} onChange={handleConstructionChange} className="w-full p-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="L" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Breite (mm)</label>
                        <input type="number" step="0.01" disabled={!isEditingMode} name="width" value={formData.construction?.width || ''} onChange={handleConstructionChange} className="w-full p-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="B" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Höhe (mm)</label>
                        <input type="number" step="0.01" disabled={!isEditingMode} name="height" value={formData.construction?.height || ''} onChange={handleConstructionChange} className="w-full p-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg text-sm dark:text-white disabled:bg-transparent disabled:border-transparent disabled:font-medium disabled:px-0" placeholder="H" />
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            )}

            {/* =========================================================
                ANSICHT: STÜCKLISTE (BOM)
            ========================================================= */}
            {activeTab === 'bom' && formData.partType === 'Eigenfertigungsteil' && (
              <div className="space-y-6 animate-fadeIn">
                <div className="flex justify-between items-end border-b dark:border-gray-700 pb-2">
                  <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">Baugruppen Stückliste (BOM)</h3>
                  <span className="text-sm text-gray-500">Basis: 1 {formData.storageUnit || 'Stk'}</span>
                </div>
                
                {!isEditingMode && (
                   <p className="text-sm text-gray-500 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">Klicken Sie oben auf "Bearbeiten", um die Stückliste anzupassen.</p>
                )}
                
                {isEditingMode && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/30">
                    <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-3">Komponente hinzufügen</h4>
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[250px]">
                        <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Artikel suchen</label>
                        <select value={newBomItem.articleId} onChange={e => setNewBomItem({...newBomItem, articleId: e.target.value})} className="w-full p-2 border-blue-200 dark:border-blue-700 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white">
                          <option value="">-- Artikel wählen --</option>
                          {articles.filter(a => a.articleId !== formData.articleId).map(a => (
                            <option key={a.id} value={a.articleId}>{a.articleId} - {a.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="w-32">
                        <label className="block text-xs text-blue-600 dark:text-blue-400 mb-1">Menge</label>
                        <input type="number" step="0.001" min="0" value={newBomItem.quantity} onChange={e => setNewBomItem({...newBomItem, quantity: e.target.value})} className="w-full p-2 border-blue-200 dark:border-blue-700 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"/>
                      </div>
                      <button onClick={() => addItemToList('bom', newBomItem, setNewBomItem, { articleId: '', quantity: 1 })} className="px-4 py-2 bg-[#0F52BA] text-white rounded-lg hover:bg-blue-800 text-sm font-medium shadow-sm">Plus</button>
                    </div>
                  </div>
                )}

                <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="p-3">Art.-Nr.</th>
                        <th className="p-3">Bezeichnung</th>
                        <th className="p-3 text-right">Menge</th>
                        {isEditingMode && <th className="p-3 text-center">Aktion</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {(!formData.bom || formData.bom.length === 0) && (
                        <tr><td colSpan={isEditingMode ? 4 : 3} className="p-4 text-center text-sm text-gray-500">Die Stückliste ist leer.</td></tr>
                      )}
                      {(formData.bom || []).map(item => {
                        const refArt = articles.find(a => a.articleId === item.articleId) || {};
                        return (
                          <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="p-3 text-sm font-mono text-[#0F52BA] cursor-pointer hover:underline" onClick={() => openArticle(refArt)}>{item.articleId}</td>
                            <td className="p-3 font-medium dark:text-white">{refArt.name || 'Unbekannt'}</td>
                            <td className="p-3 text-right font-bold dark:text-white">{item.quantity} <span className="text-xs font-normal text-gray-500">{refArt.storageUnit || ''}</span></td>
                            {isEditingMode && (
                              <td className="p-3 text-center">
                                <button onClick={() => removeItemFromList('bom', item.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* =========================================================
                ANSICHT: DOKUMENTE & ZERTIFIKATE
            ========================================================= */}
            {activeTab === 'documents' && (
              <div className="space-y-6 animate-fadeIn">
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex items-center gap-2"><FileCheck className="w-5 h-5 text-[#0F52BA]"/> Dokumente & Zertifikate</h3>
                
                {isEditingMode && (
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border dark:border-gray-700">
                    <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3">Neues Dokument hinterlegen</h4>
                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="w-40">
                        <label className="block text-xs text-gray-500 mb-1">Bereich</label>
                        <select value={newDocument.category} onChange={e => setNewDocument({...newDocument, category: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                          <option value="Einkauf">Einkauf</option>
                          <option value="Verkauf">Verkauf</option>
                          <option value="Qualität">Qualität</option>
                          <option value="Allgemein">Allgemein</option>
                        </select>
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <label className="block text-xs text-gray-500 mb-1">Art / Titel (z.B. FDA, 3.1 Materialzertifikat)</label>
                        <input type="text" value={newDocument.type} onChange={e => setNewDocument({...newDocument, type: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                      </div>
                      <div className="flex-1 min-w-[150px]">
                        <label className="block text-xs text-gray-500 mb-1">Optionale Notiz / Link</label>
                        <input type="text" value={newDocument.note} onChange={e => setNewDocument({...newDocument, note: e.target.value})} className="w-full p-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-700 dark:text-white" />
                      </div>
                      <button onClick={() => {
                          if(!newDocument.type) return;
                          addItemToList('documents', newDocument, setNewDocument, { category: 'Einkauf', type: '', note: '' });
                        }} className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors">
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                )}

                <div className="border dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="p-3 w-32">Bereich</th>
                        <th className="p-3">Dokumentenart / Titel</th>
                        <th className="p-3">Notiz</th>
                        {isEditingMode && <th className="p-3 text-center">Aktion</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {(!formData.documents || formData.documents.length === 0) && (
                        <tr><td colSpan={isEditingMode ? 4 : 3} className="p-4 text-center text-gray-500">Keine Dokumente oder Zertifikate hinterlegt.</td></tr>
                      )}
                      {(formData.documents || []).map(doc => (
                        <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="p-3 font-medium text-gray-700 dark:text-gray-300">
                            <span className={`px-2 py-1 rounded text-xs ${doc.category === 'Einkauf' ? 'bg-green-100 text-green-800' : doc.category === 'Verkauf' ? 'bg-blue-100 text-blue-800' : doc.category === 'Qualität' ? 'bg-purple-100 text-purple-800' : 'bg-gray-200 text-gray-800'}`}>
                              {doc.category}
                            </span>
                          </td>
                          <td className="p-3 font-bold dark:text-white">{doc.type}</td>
                          <td className="p-3 text-gray-500 dark:text-gray-400">{doc.note || '-'}</td>
                          {isEditingMode && (
                            <td className="p-3 text-center">
                              <button onClick={() => removeItemFromList('documents', doc.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded"><Trash2 className="w-4 h-4"/></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* =========================================================
          MODAL: ERWEITERTE LIEFERANTENSUCHE
      ========================================================= */}
      {showSupplierModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Search className="w-5 h-5 text-[#0F52BA]" /> Erweiterte Lieferantensuche
              </h2>
              <button onClick={() => setShowSupplierModal(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Filters */}
            <div className="p-6 border-b dark:border-gray-700 bg-white dark:bg-gray-900 flex gap-4">
              <div className="flex-1">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Suchbegriff (Name, ID)</label>
                <input 
                  type="text" 
                  value={supplierModalSearch} 
                  onChange={e => setSupplierModalSearch(e.target.value)}
                  placeholder="Suchen..." 
                  className="w-full p-2.5 border dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div className="w-1/3">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rubrik / Kategorie</label>
                <select 
                  value={supplierModalCategory} 
                  onChange={e => setSupplierModalCategory(e.target.value)}
                  className="w-full p-2.5 border dark:border-gray-700 rounded-lg text-sm dark:bg-gray-800 dark:text-white"
                >
                  <option value="">Alle Rubriken</option>
                  {supplierCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900/50">
              <div className="border dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-800 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="p-4 w-20">ID</th>
                      <th className="p-4">Name</th>
                      <th className="p-4">Rubrik</th>
                      <th className="p-4 text-right">Aktion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700">
                    {dbSuppliers.filter(s => {
                      const searchTerm = String(supplierModalSearch || '').toLowerCase();
                      const searchMatch = String(s.name || '').toLowerCase().includes(searchTerm) || 
                                          String(s.supplierId || '').toLowerCase().includes(searchTerm) ||
                                          String(s.category || '').toLowerCase().includes(searchTerm) ||
                                          String(s.rubrik || '').toLowerCase().includes(searchTerm);
                      const catMatch = supplierModalCategory === '' || (s.category === supplierModalCategory || s.rubrik === supplierModalCategory);
                      return searchMatch && catMatch;
                    }).length === 0 ? (
                      <tr><td colSpan="4" className="p-8 text-center text-gray-500">Keine Lieferanten gefunden.</td></tr>
                    ) : (
                      dbSuppliers.filter(s => {
                        const searchTerm = String(supplierModalSearch || '').toLowerCase();
                        const searchMatch = String(s.name || '').toLowerCase().includes(searchTerm) || 
                                            String(s.supplierId || '').toLowerCase().includes(searchTerm) ||
                                            String(s.category || '').toLowerCase().includes(searchTerm) ||
                                            String(s.rubrik || '').toLowerCase().includes(searchTerm);
                        const catMatch = supplierModalCategory === '' || (s.category === supplierModalCategory || s.rubrik === supplierModalCategory);
                        return searchMatch && catMatch;
                      }).map(sup => (
                        <tr key={sup.id} className="hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors">
                          <td className="p-4 text-gray-500 font-mono">{sup.supplierId || '-'}</td>
                          <td className="p-4 font-bold text-gray-900 dark:text-white">{sup.name}</td>
                          <td className="p-4 text-gray-600 dark:text-gray-300">
                            {sup.category || sup.rubrik ? (
                              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-xs">{sup.category || sup.rubrik}</span>
                            ) : '-'}
                          </td>
                          <td className="p-4 text-right">
                            <button 
                              onClick={() => handleSelectSupplier(sup)}
                              className="px-4 py-2 bg-[#0F52BA] text-white rounded-lg text-sm font-medium hover:bg-blue-800 transition-colors"
                            >
                              Auswählen
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};